<template>
  <div
    :itemscope="useSchema"
    :itemtype="useSchema ? 'http://schema.org/ImageObject' : undefined"
    :class="[
      'p-card',
      'product-card',
      'js-test-tile-model',
      'product-card__tile',
      { skeleton: !model.id },
      { viewed },
    ]"
    :data-lazy="lazyImg && model.id ? model.id : null"
    :data-id="null"
    :data-rid="model && model.id"
  >
    <div
      ref="card"
      class="p-card__analytics"
      :data-slot="position"
      :data-url="objToBase64({url: model.view_url})"
      :data-block="blockType"
      :data-type="'model'"
      :data-offer_id="model.id ? model.id : ''"
      :data-rid="model && model.id"
    />
    <template v-if="model.id">
      <div
        v-if="isMinPrice || discountMax >= 10"
        class="product-card__model-sticker"
      >
        <UiStickerMonitoring
          :type="isMinPrice ? 'minimal' : 'percent-sale'"
          :text="discountMax || 0"
        />
      </div>
      <DebugInfo v-if="debugPanel" :type="'model'" :data="model" />
      <div class="product-card__slider pointer">
        <div
          v-if="model.review_count || model.rating"
          :class="[
            'product-card__scores',
            model.rating ? '' : 'no-rating',
            { 'l-gap-padding': !model.review_count },
          ]"
        >
          <ratingMerchant
            :class="{ 'l-gap-padding': !model.review_count }"
            :rating="model.rating"
            :model="model"
            :modelPath="modelPath"
            :isTile="true"
          />
        </div>
        <ui-product-card-slider
          :model="model"
          :lazy-img="lazyImg"
          :index="index"
          :offers="offers"
          type="model"
          block-type="tile"
          :position="position"
          :model-path="modelPath"
          :use-schema="useSchema"
        />
      </div>
      <div class="product-card__info-description">
        <div class="offer-price">
          <Currency
            v-if="!notInStock"
            :value="ProductPrice"
            :link="modelPath"
            prefix="от"
            class="product-card__price price-s"
          />
          <div v-else class="product-card__price price-s">Нет в продаже</div>
        </div>

        <nuxt-link :to="modelPath">
          <div
            class="product-card__title body-l-medium"
            :itemprop="useSchema ? 'name' : undefined"
          >
            {{ model.title }}
          </div>
        </nuxt-link>

        <modelViews :countViews="countViews" is-atom />

        <div class="product-card__buttons">
          <ui-ui-button
            class="product-card__button"
            :loading="!model.id"
            :path="modelPath"
            size="medium"
            :resize="true"
            type="primary"
            mode="link"
            font-class="body-m-regular"
            :counter="model.offer_count"
            @click="sendYaSravnitCeni()"
          >
            <template v-if="model.offer_count > 0"><span class="desktop-only">Сравнить цены</span><span class="mobile-only">Все цены</span></template>
            <template v-else>Смотреть похожие</template>
          </ui-ui-button>
        </div>
        <div class="price-card__basement">
          <storeRating
            v-if="shopRating"
            :countShops="shopRating"
            :merchantRating="merchantRatingCount"
          />
          <emptyShopRating v-else />
        </div>
      </div>
      <div v-if="isBot" :itemprop="useSchema ? 'description' : undefined">
        <div
          v-if="model.description || model.specifications"
          class="p-card__characteristic-text"
        >
          <span
            v-for="(spec, index) in model.specifications"
            :key="index"
            class="specifications"
          >
            {{ index > 0 ? spec.title.toLowerCase() : spec.title }}:
            {{ spec.value }}
            <span
              v-show="index < model.specifications?.length - 1"
              class="comma"
              >,
            </span>
          </span>
        </div>
      </div>
    </template>
    <template v-else>
      <div class="p-card__slider">
        <div class="p-card__skeleton_img" />
      </div>
      <div class="p-card__info-description">
        <div class="p-card__title" />
      </div>

      <div class="p-card__price" />
      <div class="p-card__info-price">
        <div v-if="!isMobile" class="p-card__price-comparison">
          <div v-for="n in 4" :key="n" class="p-card__offers" />
        </div>
      </div>
    </template>
  </div>
</template>
<script setup lang="ts">
import storeRating from '~/components/ui/storeRating/storeRating.vue';
import emptyShopRating from '~/components/ui/EmptyShopRating.vue';
import modelViews from '~/components/page-model/model-views/ModelViews.vue';
import ratingMerchant from '~/components/ui/rating-merchant/rating-merchant.vue';
import type { ModelItem } from '~/interfaces/api';
import { useFavoriteStore } from '~/stores/favorite';
import { useCompareStore } from '~/stores/compare';
import { useMediaQueryStore } from '~/stores/mediaQuery';
import DebugInfo from '~/components/debug-info/DebugInfo.vue';
import Currency from '~/components/ui/currency/Currency.vue';
import { useAnalyticsStore } from '~/stores/analytics';
import { useGlobalStore } from '~/stores/global';
import { useInteractiveStore } from '~/stores/interactive';
import {objToBase64} from "~/utils/base64";

const props = defineProps({
  model: {
    type: Object as () => any,
  },
  offers: Array,
  lazyImg: {
    type: Boolean,
    default: false,
  },
  nuxtLazy: {
    type: Boolean,
    default: false,
  },
  viewed: {
    type: Boolean,
    default: false,
  },
  isBot: {
    type: Boolean,
    default: false,
  },
  position: {
    type: Number,
    default: 1,
  },
  useSchema: Boolean,
  blockType: {
    type: String,
    default: 'listing',
  },
  addFilterQuery: {
    type: String,
    default: '',
  },
  index: {
    type: Number,
    default: 0,
  },
  discountMax: {
    type: Number,
    default: 0,
  },
  shopRatingHighCount: {
    type: Number,
    default: 0,
  },
  visitCount: {
    type: Number,
    default: 0,
  },
  isCarousel: {
    type: Boolean,
    default: false,
  },
});

const countViews = computed(() => {
  return props.model?.visit_count || props.visitCount;
});
// TODO Заменить на протоколы получения данных когда сделают бэк
const merchantRatingCount = 4.5;
// TODO end
const item = ref<Number>(0);
const card = ref<HTMLElement | undefined>();
const showButtonMoreOnDesktop = ref(false);
const favoriteStore = useFavoriteStore();
const compareStore = useCompareStore();
const mediaStore = useMediaQueryStore();
const analyticsStore = useAnalyticsStore();
const globalStore = useGlobalStore();
const interactiveStore = useInteractiveStore();
const shopRating = computed(
  () => props.model?.shop_rating_high_count || props.shopRatingHighCount
);
const debugPanel = computed(() => globalStore.debugPanel);
const isMobile = computed(() => mediaStore.isMobile);
const { $ymAnyEventWithParam } = useNuxtApp();
const ProductPrice = computed(() => {
  if (props.model) return props.model?.price_info?.min || '';
  return '';
});
const isMinPrice = computed(() => props.model?.has_min_price);
const notInStock = computed(() => {
  if (props.model) return !props.model.offer_count;
  return null;
});

const inCompare = computed(
  () =>
    props.model &&
    compareStore.ids.find(
      (id: number) => Number(id) === Number(props.model?.id)
    )
);

onMounted(() => {
  if (card.value)
    nextTick(() => {
      if (card.value) {
        analyticsStore.observeView(card.value);
      }
      if (!props.isBot) {
        showButtonMoreOnDesktop.value = true;
      }
    });
});

onBeforeUnmount(() => {
  if (card.value) analyticsStore.unobserveView(card.value);
});

const modelPath = computed(() => {
  const model = <ModelItem>props.model;
  if (!model) return '';
  const slug = model?.slug || '';
  if (!props.addFilterQuery) return `/${slug}/`;
  return `/${slug}/?${props.addFilterQuery}`;
});

const favorite = computed(
  () =>
    props.model &&
    favoriteStore.ids.find(
      (id: number) => Number(id) === Number(props.model?.id)
    )
);
const toggleFavorite = () => {
  if (props.model)
    favoriteStore.toggleFavorite({
      id: props.model.id,
      catId: +(<ModelItem>props.model).category.id,
      deleteCard: !!favorite.value,
    });
};
const toggleCompare = () => {
  if (props.model) {
    compareStore.toggleCompare({
      id: props.model.id,
      catId: +(<ModelItem>props.model).category.id,
      deleteCard: !!inCompare.value,
    });
  }
};

const sendYaSravnitCeni = () => {
  $ymAnyEventWithParam('reachGoal', 'click_na_sravnit_ceni', {
    discount_max: props.discountMax,
    visit_count: props.visitCount,
    shop_rating_high_count: props.shopRatingHighCount,
  });
};

</script>
<style scoped lang="scss" src="./productCardTileModelScss.scss" />
